import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { buildApiUrl, toUploadUrl } from '../../config/api';
import '../../styles/admin-addon-services.css';

const categoryOptions = [
    'dining',
    'celebration',
    'romance',
    'transport',
    'activity',
    'hospitality',
    'insurance',
    'wellness',
    'adventure',
    'other'
];

const priceTypeOptions = [
    { value: 'fixed', label: 'Fixed Price' },
    { value: 'per_person', label: 'Per Person' },
    { value: 'per_day', label: 'Per Day' },
    { value: 'per_room', label: 'Per Room' }
];

const initialFormState = {
    title: '',
    short_description: '',
    category: 'dining',
    price: '',
    price_type: 'fixed',
    status: 'active',
    applicable_scope: 'all',
    applicable_package_ids: '',
    applicable_package_categories: '',
    applicable_destination_keywords: '',
    sort_order: 0
};

const AddonServices = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        category: 'all',
        status: 'all',
        price_type: 'all'
    });

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Session expired. Please login again.');
            window.location.href = '/admin/login';
            return null;
        }
        return token;
    };

    const fetchServices = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl('/addon-services/admin/all'), {
                params: filters,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            setServices(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching add-on services:', error);
            alert(error.response?.data?.message || 'Failed to fetch add-on services');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const stats = useMemo(() => ({
        total: services.length,
        active: services.filter((service) => service.status === 'active').length,
        categories: new Set(services.map((service) => service.category)).size
    }), [services]);

    const resetForm = () => {
        setSelectedService(null);
        setFormData(initialFormState);
        setSelectedImage(null);
        setPreviewUrl('');
    };

    const openCreateModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (service) => {
        setSelectedService(service);
        setFormData({
            title: service.title || '',
            short_description: service.short_description || '',
            category: service.category || 'dining',
            price: service.price || '',
            price_type: service.price_type || 'fixed',
            status: service.status || 'active',
            applicable_scope: service.applicable_scope || 'all',
            applicable_package_ids: Array.isArray(service.applicable_package_ids) ? service.applicable_package_ids.join(', ') : '',
            applicable_package_categories: Array.isArray(service.applicable_package_categories) ? service.applicable_package_categories.join(', ') : '',
            applicable_destination_keywords: Array.isArray(service.applicable_destination_keywords) ? service.applicable_destination_keywords.join(', ') : '',
            sort_order: service.sort_order || 0
        });
        setSelectedImage(null);
        setPreviewUrl(service.image ? toUploadUrl(service.image) : '');
        setShowModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please upload a valid image');
            return;
        }

        setSelectedImage(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = getToken();
        if (!token) return;

        try {
            setSaving(true);

            const payload = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                payload.append(key, value ?? '');
            });

            if (selectedImage) {
                payload.append('image', selectedImage);
            }

            const url = selectedService
                ? buildApiUrl(`/addon-services/${selectedService.id}/update`)
                : buildApiUrl('/addon-services');

            await axios.post(url, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-auth-token': token,
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert(`Add-on service ${selectedService ? 'updated' : 'created'} successfully`);
            setShowModal(false);
            resetForm();
            fetchServices();
        } catch (error) {
            console.error('Error saving add-on service:', error);
            alert(error.response?.data?.message || 'Failed to save add-on service');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusToggle = async (service) => {
        const token = getToken();
        if (!token) return;

        try {
            await axios.post(buildApiUrl(`/addon-services/${service.id}/status`), {
                status: service.status === 'active' ? 'inactive' : 'active'
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            fetchServices();
        } catch (error) {
            console.error('Error updating add-on status:', error);
            alert(error.response?.data?.message || 'Failed to update service status');
        }
    };

    const handleDelete = async (serviceId) => {
        if (!window.confirm('Are you sure you want to delete this add-on service?')) return;

        const token = getToken();
        if (!token) return;

        try {
            // Some production servers block DELETE; use POST fallback endpoint.
            await axios.post(
                buildApiUrl(`/addon-services/${serviceId}/delete`),
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-auth-token': token
                    }
                }
            );
            fetchServices();
        } catch (error) {
            console.error('Error deleting add-on service:', error);
            alert(error.response?.data?.message || 'Failed to delete add-on service');
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader title="Trip Add-on Services" subtitle="Create paid extras customers can add during booking" />

                <div className="addon-admin-page">
                    <div className="addon-admin-topbar">
                        <div className="addon-stats-grid">
                            <div className="addon-stat-card">
                                <span>Total Services</span>
                                <strong>{stats.total}</strong>
                            </div>
                            <div className="addon-stat-card">
                                <span>Active Services</span>
                                <strong>{stats.active}</strong>
                            </div>
                            <div className="addon-stat-card">
                                <span>Categories</span>
                                <strong>{stats.categories}</strong>
                            </div>
                        </div>
                        <button className="addon-primary-btn" onClick={openCreateModal}>
                            <i className="fas fa-plus"></i> Add Add-on Service
                        </button>
                    </div>

                    <div className="addon-filters-card">
                        <input
                            type="text"
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Search by title or description"
                        />
                        <select name="category" value={filters.category} onChange={handleFilterChange}>
                            <option value="all">All Categories</option>
                            {categoryOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        <select name="price_type" value={filters.price_type} onChange={handleFilterChange}>
                            <option value="all">All Price Types</option>
                            {priceTypeOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <select name="status" value={filters.status} onChange={handleFilterChange}>
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    <div className="addon-list-card">
                        {loading ? (
                            <div className="addon-empty-state">Loading add-on services...</div>
                        ) : services.length === 0 ? (
                            <div className="addon-empty-state">No add-on services found for current filters.</div>
                        ) : (
                            <div className="addon-table-wrap">
                                <table className="addon-table">
                                    <thead>
                                        <tr>
                                            <th>Service</th>
                                            <th>Category</th>
                                            <th>Price</th>
                                            <th>Applicability</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {services.map((service) => (
                                            <tr key={service.id}>
                                                <td>
                                                    <div className="addon-service-cell">
                                                        <img
                                                            src={toUploadUrl(service.image, { fallback: 'https://placehold.co/80x80?text=Addon' })}
                                                            alt={service.title}
                                                        />
                                                        <div>
                                                            <strong>{service.title}</strong>
                                                            <p>{service.short_description || 'No description added'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className="addon-chip">{service.category}</span></td>
                                                <td>₹{Number(service.price || 0).toLocaleString('en-IN')} <small>/{service.price_type.replace('_', ' ')}</small></td>
                                                <td>{service.applicable_scope === 'all' ? 'All packages' : 'Specific rules'}</td>
                                                <td>
                                                    <button
                                                        className={`status-toggle ${service.status}`}
                                                        onClick={() => handleStatusToggle(service)}
                                                    >
                                                        {service.status}
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="addon-actions">
                                                        <button onClick={() => openEditModal(service)} className="icon-action edit">
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button onClick={() => handleDelete(service.id)} className="icon-action delete">
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="addon-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{selectedService ? 'Edit Add-on Service' : 'Create Add-on Service'}</h2>
                                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                            </div>

                            <form onSubmit={handleSubmit} className="addon-form">
                                <div className="addon-form-grid">
                                    <div className="form-group">
                                        <label>Title *</label>
                                        <input type="text" name="title" value={formData.title} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Category *</label>
                                        <select name="category" value={formData.category} onChange={handleInputChange}>
                                            {categoryOptions.map((option) => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Short Description</label>
                                        <textarea name="short_description" rows="3" value={formData.short_description} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Price *</label>
                                        <input type="number" min="0" step="0.01" name="price" value={formData.price} onChange={handleInputChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Price Type *</label>
                                        <select name="price_type" value={formData.price_type} onChange={handleInputChange}>
                                            {priceTypeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select name="status" value={formData.status} onChange={handleInputChange}>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Sort Order</label>
                                        <input type="number" min="0" name="sort_order" value={formData.sort_order} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Applicability</label>
                                        <select name="applicable_scope" value={formData.applicable_scope} onChange={handleInputChange}>
                                            <option value="all">All Packages</option>
                                            <option value="specific">Specific Packages / Destinations</option>
                                        </select>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Applicable Package IDs</label>
                                        <input type="text" name="applicable_package_ids" value={formData.applicable_package_ids} onChange={handleInputChange} placeholder="Example: 1, 2, 5" />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Applicable Package Categories</label>
                                        <input type="text" name="applicable_package_categories" value={formData.applicable_package_categories} onChange={handleInputChange} placeholder="Example: honeymoon, corporate" />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Applicable Destination Keywords</label>
                                        <input type="text" name="applicable_destination_keywords" value={formData.applicable_destination_keywords} onChange={handleInputChange} placeholder="Example: kashmir, gulmarg" />
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Image</label>
                                        <input type="file" accept="image/*" onChange={handleImageChange} />
                                    </div>
                                </div>

                                {previewUrl && (
                                    <div className="addon-preview-card">
                                        <img src={previewUrl} alt="Add-on preview" />
                                    </div>
                                )}

                                <div className="modal-actions">
                                    <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="save-btn" disabled={saving}>
                                        {saving ? 'Saving...' : selectedService ? 'Update Service' : 'Create Service'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddonServices;
